// Excel import/export utilities using XLSX library

import * as XLSX from 'xlsx'
import type {
  TimetableRecordWithRelations,
  Teacher,
  Subject,
  ClassSection,
  SchedulePeriod,
  DayOfWeek,
} from '@timetable/types'
import { formatDayOfWeek, formatTimeRange } from './formatting'
import { isValidDayOfWeek } from './validation'

// Excel column mapping for import
export interface ExcelTimetableRow {
  class: string
  section: string
  day: string
  period: number
  teacher: string
  subject: string
}

// Parse Excel file to JSON
export async function parseExcelFile(file: File | Blob): Promise<ExcelTimetableRow[]> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()

    reader.onload = (e: ProgressEvent<FileReader>) => {
      try {
        const data = e.target?.result
        const workbook = XLSX.read(data, { type: 'binary' })
        const sheetName = workbook.SheetNames[0]
        const worksheet = workbook.Sheets[sheetName]
        const jsonData = XLSX.utils.sheet_to_json<ExcelTimetableRow>(worksheet)
        resolve(jsonData)
      } catch (error) {
        const errorMessage = error instanceof Error ? error.message : 'Unknown error'
        reject(new Error(`Failed to parse Excel file: ${errorMessage}`))
      }
    }

    reader.onerror = () => {
      reject(new Error('Failed to read file'))
    }

    reader.readAsBinaryString(file)
  })
}

// Validation result for Excel import
export interface ExcelValidationResult {
  valid: boolean
  errors: Array<{
    row: number
    field: string
    message: string
  }>
  warnings: Array<{
    row: number
    message: string
  }>
}

// Validate Excel data
export function validateExcelData(
  data: ExcelTimetableRow[],
  teachers: Teacher[],
  subjects: Subject[],
  classSections: ClassSection[]
): ExcelValidationResult {
  const errors: Array<{ row: number; field: string; message: string }> = []
  const warnings: Array<{ row: number; message: string }> = []

  data.forEach((row, index) => {
    const rowNumber = index + 2 // Excel rows start at 1, header is row 1

    // Validate required fields
    if (!row.class || row.class.trim() === '') {
      errors.push({
        row: rowNumber,
        field: 'class',
        message: 'Class is required',
      })
    }

    if (!row.section || row.section.trim() === '') {
      errors.push({
        row: rowNumber,
        field: 'section',
        message: 'Section is required',
      })
    }

    if (!row.day || row.day.trim() === '') {
      errors.push({
        row: rowNumber,
        field: 'day',
        message: 'Day is required',
      })
    } else if (!isValidDayOfWeek(row.day.toUpperCase())) {
      errors.push({
        row: rowNumber,
        field: 'day',
        message: 'Invalid day of week',
      })
    }

    if (row.period === undefined || row.period === null) {
      errors.push({
        row: rowNumber,
        field: 'period',
        message: 'Period is required',
      })
    } else if (!Number.isInteger(row.period) || row.period < 0 || row.period > 9) {
      errors.push({
        row: rowNumber,
        field: 'period',
        message: 'Period must be between 0 and 9',
      })
    }

    if (!row.teacher || row.teacher.trim() === '') {
      errors.push({
        row: rowNumber,
        field: 'teacher',
        message: 'Teacher is required',
      })
    } else {
      // Check if teacher exists
      const teacherExists = teachers.some((t) => t.name === row.teacher.trim())
      if (!teacherExists) {
        errors.push({
          row: rowNumber,
          field: 'teacher',
          message: `Teacher "${row.teacher}" not found`,
        })
      }
    }

    if (!row.subject || row.subject.trim() === '') {
      errors.push({
        row: rowNumber,
        field: 'subject',
        message: 'Subject is required',
      })
    } else {
      // Check if subject exists
      const subjectExists = subjects.some((s) => s.subject_name === row.subject.trim())
      if (!subjectExists) {
        errors.push({
          row: rowNumber,
          field: 'subject',
          message: `Subject "${row.subject}" not found`,
        })
      }
    }

    // Check if class-section combination exists
    if (row.class && row.section) {
      const sectionExists = classSections.some(
        (cs: any) =>
          cs.class?.class_name === row.class.trim() && cs.section === row.section.trim()
      )
      if (!sectionExists) {
        errors.push({
          row: rowNumber,
          field: 'section',
          message: `Class section "${row.class}-${row.section}" not found`,
        })
      }
    }
  })

  return {
    valid: errors.length === 0,
    errors,
    warnings,
  }
}

// Grid format for export
export interface TimetableGridCell {
  teacher: string
  subject: string
  colorCode?: string
}

export type TimetableGrid = Record<DayOfWeek, Record<number, TimetableGridCell | null>>

// Transform timetable records to grid format
export function transformToGrid(
  records: TimetableRecordWithRelations[],
  periods: SchedulePeriod[]
): TimetableGrid {
  const grid: TimetableGrid = {
    MONDAY: {},
    TUESDAY: {},
    WEDNESDAY: {},
    THURSDAY: {},
    FRIDAY: {},
    SATURDAY: {},
    SUNDAY: {},
  }

  // Initialize all cells as null using period_number (day comes from records)
  periods.forEach((period) => {
    Object.keys(grid).forEach((day) => {
      grid[day as DayOfWeek][period.period_number] = null
    })
  })

  // Fill in the records — day comes from record.day_of_week
  records.forEach((record) => {
    const day = record.day_of_week as DayOfWeek
    const periodNumber = record.period.period_number

    grid[day][periodNumber] = {
      teacher: record.teacher.name,
      subject: record.subject.subject_name,
      colorCode: record.subject.color_code || undefined,
    }
  })

  return grid
}

// Generate Excel workbook from timetable grid
export function generateExcelFromGrid(
  grid: TimetableGrid,
  periods: SchedulePeriod[],
  title: string,
  schoolName?: string,
  academicYear?: string
): XLSX.WorkBook {
  const workbook = XLSX.utils.book_new()

  // Group periods by day — SchedulePeriod has no day column; use all days
  const periodsByDay: Record<DayOfWeek, SchedulePeriod[]> = {
    MONDAY: [],
    TUESDAY: [],
    WEDNESDAY: [],
    THURSDAY: [],
    FRIDAY: [],
    SATURDAY: [],
    SUNDAY: [],
  }

  // All periods apply to all days (day is on timetable_records, not schedule_periods)
  periods.forEach((period) => {
    Object.keys(periodsByDay).forEach((day) => {
      periodsByDay[day as DayOfWeek].push(period)
    })
  })

  // Sort periods by period number
  Object.keys(periodsByDay).forEach((day) => {
    periodsByDay[day as DayOfWeek].sort((a, b) => a.period_number - b.period_number)
  })

  // Create worksheet data
  const worksheetData: any[][] = []

  // Add header rows
  if (schoolName) {
    worksheetData.push([schoolName])
  }
  if (academicYear) {
    worksheetData.push([`Academic Year: ${academicYear}`])
  }
  worksheetData.push([title])
  worksheetData.push([]) // Empty row

  // Get all unique period numbers
  const allPeriodNumbers = Array.from(
    new Set(periods.map((p) => p.period_number))
  ).sort((a, b) => a - b)

  // Create header row with days
  const days: DayOfWeek[] = ['MONDAY', 'TUESDAY', 'WEDNESDAY', 'THURSDAY', 'FRIDAY']
  const headerRow = ['Period', ...days.map((day) => formatDayOfWeek(day))]
  worksheetData.push(headerRow)

  // Create data rows for each period
  allPeriodNumbers.forEach((periodNumber) => {
    const row: any[] = [`Period ${periodNumber}`]

    days.forEach((day) => {
      const cell = grid[day][periodNumber]
      if (cell) {
        row.push(`${cell.subject}\n${cell.teacher}`)
      } else {
        row.push('')
      }
    })

    worksheetData.push(row)
  })

  // Create worksheet
  const worksheet = XLSX.utils.aoa_to_sheet(worksheetData)

  // Set column widths
  const columnWidths = [{ wch: 15 }, ...days.map(() => ({ wch: 25 }))]
  worksheet['!cols'] = columnWidths

  // Add worksheet to workbook
  XLSX.utils.book_append_sheet(workbook, worksheet, 'Timetable')

  return workbook
}

// Generate Excel file for download
export function generateExcelFile(
  records: TimetableRecordWithRelations[],
  periods: SchedulePeriod[],
  title: string,
  schoolName?: string,
  academicYear?: string
): Blob {
  const grid = transformToGrid(records, periods)
  const workbook = generateExcelFromGrid(grid, periods, title, schoolName, academicYear)

  // Write workbook to binary string
  const excelBuffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' })

  // Create blob
  return new Blob([excelBuffer], {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  })
}

// Generate filename for export
export function generateExportFilename(
  viewMode: 'class' | 'teacher' | 'subject',
  entityName: string,
  date?: Date
): string {
  const dateStr = date
    ? date.toISOString().split('T')[0]
    : new Date().toISOString().split('T')[0]

  const sanitizedName = entityName.replace(/[^a-z0-9]/gi, '_')

  return `timetable_${viewMode}_${sanitizedName}_${dateStr}.xlsx`
}

// Generate Excel template for import
export function generateImportTemplate(): Blob {
  const workbook = XLSX.utils.book_new()

  // Create template data with example row
  const templateData = [
    ['class', 'section', 'day', 'period', 'teacher', 'subject'],
    ['I', 'A', 'MONDAY', 0, 'John Doe', 'Mathematics'],
    ['I', 'A', 'MONDAY', 1, 'Jane Smith', 'English'],
  ]

  const worksheet = XLSX.utils.aoa_to_sheet(templateData)

  // Set column widths
  worksheet['!cols'] = [
    { wch: 10 },
    { wch: 10 },
    { wch: 12 },
    { wch: 8 },
    { wch: 20 },
    { wch: 20 },
  ]

  XLSX.utils.book_append_sheet(workbook, worksheet, 'Timetable Import')

  const excelBuffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' })

  return new Blob([excelBuffer], {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  })
}

// Export list format (not grid)
export function generateExcelListFormat(
  records: TimetableRecordWithRelations[],
  title: string,
  schoolName?: string,
  academicYear?: string
): Blob {
  const workbook = XLSX.utils.book_new()

  // Create worksheet data
  const worksheetData: any[][] = []

  // Add header rows
  if (schoolName) {
    worksheetData.push([schoolName])
  }
  if (academicYear) {
    worksheetData.push([`Academic Year: ${academicYear}`])
  }
  worksheetData.push([title])
  worksheetData.push([]) // Empty row

  // Add column headers
  worksheetData.push([
    'Class',
    'Section',
    'Day',
    'Period',
    'Time',
    'Subject',
    'Teacher',
  ])

  // Add data rows
  records.forEach((record) => {
    worksheetData.push([
      (record.class_section as any).class?.class_name || '',
      record.class_section.section,
      formatDayOfWeek(record.day_of_week as DayOfWeek),
      record.period.period_number,
      formatTimeRange(record.period.time_start, record.period.time_end),
      record.subject.subject_name,
      record.teacher.name,
    ])
  })

  // Create worksheet
  const worksheet = XLSX.utils.aoa_to_sheet(worksheetData)

  // Set column widths
  worksheet['!cols'] = [
    { wch: 10 },
    { wch: 10 },
    { wch: 12 },
    { wch: 8 },
    { wch: 15 },
    { wch: 20 },
    { wch: 20 },
  ]

  XLSX.utils.book_append_sheet(workbook, worksheet, 'Timetable')

  const excelBuffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' })

  return new Blob([excelBuffer], {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  })
}

// ============================================================================
// Import/Export Wizard Excel utilities
// ============================================================================

import type {
  ParsedMasterData,
  ParsedTeacher,
  ParsedSubject,
  ParsedClass,
  ParsedRoom,
  ParsedPeriod,
  ParsedClassSchedule,
  ParsedClassScheduleAssignment,
  ParsedTeacherSubject,
  ParsedTeacherClass,
  ParsedSubjectClass,
  TimetableRow,
  ValidationError,
  MasterDataValidationReport,
  ExportData,
} from '@timetable/types'

// MasterDataSnapshot is the existing school data used to pre-fill templates
export type MasterDataSnapshot = ParsedMasterData

const XLSX_MIME = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'

// ── Tab / column definitions ──────────────────────────────────────────────────

const TEACHER_COLS = ['Name', 'Email', 'Phone', 'Teacher_Type']
const SUBJECT_COLS = ['Subject_Name', 'Subject_Code', 'Color_Code']
const CLASS_COLS = ['Class_Name', 'Division', 'Section', 'Room', 'Class_Teacher']
const ROOM_COLS = ['Room_Name', 'Description', 'Capacity']
const VALID_SECTIONS = ['A', 'B', 'C', 'D', 'E', 'F']
const PERIOD_COLS = ['Schedule_Name', 'Period_Number', 'Start_Time', 'End_Time', 'Is_Break', 'Period_Name', 'Break_Type']
const CLASS_SCHEDULE_COLS = ['Schedule_Name', 'Description', 'Working_Days']
const SCHEDULE_ASSIGNMENT_COLS = ['Class_Name', 'Schedule_Name']
const TEACHER_SUBJECT_COLS = ['Teacher_Name', 'Subject_Name']
const TEACHER_CLASS_COLS = ['Teacher_Name', 'Class_Name']
const SUBJECT_CLASS_COLS = ['Subject_Name', 'Class_Name']
const TIMETABLE_COLS = ['Class', 'Section', 'Day', 'Period', 'Teacher', 'Subject']
const VALID_DAYS = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday']

// ── Helper: add data validation to a worksheet ────────────────────────────────
// SheetJS (free) doesn't natively write dataValidations, so we inject raw OOXML
// into the worksheet's xml fragment which gets embedded in the xlsx file.

function addDropdown(ws: XLSX.WorkSheet, sqref: string, values: string[]): void {
  if (values.length === 0) return

  // Escape XML special chars in each value
  const escape = (s: string) =>
    s.replace(/&/g, '&amp;').replace(/"/g, '&quot;').replace(/</g, '&lt;').replace(/>/g, '&gt;')

  const formula = '"' + values.map(escape).join(',') + '"'

  const dvXml =
    `<dataValidation type="list" allowBlank="1" showInputMessage="1" showErrorMessage="1" sqref="${sqref}">` +
    `<formula1>${formula}</formula1>` +
    `</dataValidation>`

  // Accumulate all validations on the worksheet object
  if (!(ws as any)['!dvNodes']) (ws as any)['!dvNodes'] = []
  ;(ws as any)['!dvNodes'].push(dvXml)
}

// Call this after all addDropdown calls and before book_append_sheet
// to flush the accumulated dataValidation XML into the worksheet
function flushDropdowns(ws: XLSX.WorkSheet): void {
  const nodes: string[] = (ws as any)['!dvNodes']
  if (!nodes || nodes.length === 0) return

  const xml =
    `<dataValidations count="${nodes.length}">` +
    nodes.join('') +
    `</dataValidations>`

  // SheetJS reads ws['!xml'] and appends it after the sheetData element
  ws['!xml'] = ((ws['!xml'] as string) ?? '') + xml
  delete (ws as any)['!dvNodes']
}

// Force time cells to string type to prevent Excel from converting "08:45" to a date serial
function forceStringColumn(ws: XLSX.WorkSheet, colLetter: string, startRow: number, endRow: number): void {
  for (let r = startRow; r <= endRow; r++) {
    const ref = `${colLetter}${r}`
    if (ws[ref]) {
      // Re-write as explicit string cell
      ws[ref] = { t: 's', v: String(ws[ref].v ?? ''), z: '@' }
    } else {
      ws[ref] = { t: 's', v: '', z: '@' }
    }
  }
}

// ── Helper: set cell merges spanning all columns ──────────────────────────────
function mergeRow(ws: XLSX.WorkSheet, rowIndex: number, colCount: number): void {
  if (!ws['!merges']) ws['!merges'] = []
  ws['!merges'].push({
    s: { r: rowIndex, c: 0 },
    e: { r: rowIndex, c: colCount - 1 },
  })
}

// ── Instructions tab content ──────────────────────────────────────────────────

function buildInstructionsSheet(): XLSX.WorkSheet {
  const data: any[][] = [
    // Row 0: title (merged)
    ['📋  Import / Export Wizard — Master Data Template'],
    // Row 1: subtitle (merged)
    ['Each data tab has instruction rows at the top. Your data header is in row 4, and data entry starts from row 5.'],
    // Row 2: blank
    [],
    // Row 3: column headers
    ['Tab', 'Column', 'Required?', 'Description', 'Possible Values / Format', 'Example'],
    // Teachers
    ['Teachers', 'Name', 'Required ✅', 'Full name of the teacher', 'Any text', 'John Smith'],
    ['Teachers', 'Email', 'Optional', 'Teacher email address', 'Valid email format', 'john@school.edu'],
    ['Teachers', 'Phone', 'Optional', 'Contact phone number', 'Any text', '+1234567890'],
    ['Teachers', 'Teacher_Type', 'Required ✅', 'Category of teacher — select from dropdown', 'Permanent / Contract / Guest / Part-Time / General', 'Permanent'],
    // Subjects
    ['Subjects', 'Subject_Name', 'Required ✅', 'Full name of the subject', 'Any text', 'Mathematics'],
    ['Subjects', 'Subject_Code', 'Optional', 'Short code for the subject', 'Any text (short)', 'MATH'],
    ['Subjects', 'Color_Code', 'Optional', 'Hex color for subject display in timetable', '#RRGGBB hex format', '#4CAF50'],
    // Classes
    ['Classes', 'Class_Name', 'Required ✅', 'Class or grade name', 'Any text', 'Grade 1'],
    ['Classes', 'Division', 'Required ✅', 'Division name (e.g. Primary, Secondary, Junior)', 'Any text — e.g. Primary / Secondary / Junior / Senior', 'Primary'],
    ['Classes', 'Section', 'Required ✅', 'Section letter — select from dropdown', 'A / B / C / D / E / F', 'A'],
    ['Classes', 'Room', 'Optional', 'Room number or name', 'Any text', 'Room 101'],
    ['Classes', 'Class_Teacher', 'Optional', 'Class teacher — must match a name in Teachers tab', 'Must exist in Teachers tab', 'John Smith'],
    // Rooms
    ['Rooms', 'Room_Name', 'Required ✅', 'Unique name for this room', 'Any text', 'Room 101'],
    ['Rooms', 'Description', 'Optional', 'Short description', 'Any text', 'Science Lab'],
    ['Rooms', 'Capacity', 'Optional', 'Maximum number of students', 'Integer', '30'],
    // Class-Schedules
    ['Class-Schedules', 'Schedule_Name', 'Required ✅', 'Unique name for this schedule', 'Any text', 'Primary Schedule'],
    ['Class-Schedules', 'Description', 'Optional', 'Short description for admins', 'Any text', 'For grades 1-5'],
    ['Class-Schedules', 'Working_Days', 'Required ✅', 'Comma-separated working days in UPPER CASE', 'MONDAY,TUESDAY,WEDNESDAY,THURSDAY,FRIDAY', 'MONDAY,TUESDAY,WEDNESDAY,THURSDAY,FRIDAY'],
    // Schedule-Periods
    ['Schedule-Periods', 'Schedule_Name', 'Required ✅', 'Must match a Schedule_Name from Class-Schedules tab', 'Must exist in Class-Schedules tab', 'Primary Schedule'],
    ['Schedule-Periods', 'Period_Number', 'Required ✅', 'Numeric period index (1, 2, 3 …)', 'Integer >= 1', '1'],
    ['Schedule-Periods', 'Start_Time', 'Required ✅', 'Period start time — type as text HH:MM', 'HH:MM (24-hour)', '08:00'],
    ['Schedule-Periods', 'End_Time', 'Required ✅', 'Period end time — type as text HH:MM', 'HH:MM (24-hour)', '08:45'],
    ['Schedule-Periods', 'Is_Break', 'Required ✅', 'Is this a break/recess? — select dropdown', 'TRUE / FALSE', 'FALSE'],
    ['Schedule-Periods', 'Period_Name', 'Optional', 'Label for this period e.g. Lunch Break', 'Any text', 'Lunch Break'],
    ['Schedule-Periods', 'Break_Type', 'Optional', 'Type of break (only if Is_Break = TRUE)', 'short / lunch / recess', 'lunch'],
    // Schedule-Assignments
    ['Schedule-Assignments', 'Class_Name', 'Required ✅', 'Must match a Class_Name from Classes tab', 'Must exist in Classes tab', 'Grade 1'],
    ['Schedule-Assignments', 'Schedule_Name', 'Required ✅', 'Must match a Schedule_Name from Class-Schedules tab', 'Must exist in Class-Schedules tab', 'Primary Schedule'],
    // Teacher-Subjects
    ['Teacher-Subjects', 'Teacher_Name', 'Required ✅', 'Must exactly match a Name from the Teachers tab', 'Must exist in Teachers tab', 'John Smith'],
    ['Teacher-Subjects', 'Subject_Name', 'Required ✅', 'Must exactly match a Subject_Name from the Subjects tab', 'Must exist in Subjects tab', 'Mathematics'],
  ]

  const ws = XLSX.utils.aoa_to_sheet(data)
  ws['!cols'] = [{ wch: 18 }, { wch: 16 }, { wch: 14 }, { wch: 56 }, { wch: 52 }, { wch: 22 }]

  // Merge title and subtitle rows across all 6 columns
  mergeRow(ws, 0, 6)
  mergeRow(ws, 1, 6)

  return ws
}

// ── Per-tab instruction rows (exactly 3 rows before the header) ───────────────
// Layout:
//   Row 1 (merged): tab title
//   Row 2 (merged): column-by-column description as a single readable paragraph
//   Row 3: blank separator
//   Row 4: actual data header  ← INSTRUCTION_ROW_COUNT = 3
//   Row 5+: data entry
// IMPORTANT: parseMasterDataWorkbook uses range: INSTRUCTION_ROW_COUNT (0-indexed)

interface ColDef {
  name: string
  required: boolean
  description: string
  possibleValues: string
  example: string
}

function buildTabInstructionRows(tabTitle: string, cols: ColDef[], colCount: number): { rows: any[][], merges: XLSX.Range[] } {
  const title = `📋  ${tabTitle}  —  Fill in data from row 5 onwards.  Header is row 4.  (* = Required)`

  // One line per column, all in cell A2 as a single string
  const lines = cols.map(
    (c) => `  • ${c.name}${c.required ? ' *' : ''}:  ${c.description}.  Values: ${c.possibleValues}.  Example: ${c.example}`
  )
  const summary = lines.join('\n')

  const rows: any[][] = [
    [title],   // row 1 — merged across all cols
    [summary], // row 2 — merged across all cols
    [],        // row 3 — blank
  ]

  const merges: XLSX.Range[] = [
    { s: { r: 0, c: 0 }, e: { r: 0, c: colCount - 1 } }, // title row
    { s: { r: 1, c: 0 }, e: { r: 1, c: colCount - 1 } }, // summary row
  ]

  return { rows, merges }
}

function makeTabSheet(
  tabTitle: string,
  cols: ColDef[],
  header: string[],
  dataRows: any[][],
  colWidths: number[],
  dropdownFn?: (ws: XLSX.WorkSheet, dataStartRow: number) => void
): XLSX.WorkSheet {
  const { rows: instrRows, merges } = buildTabInstructionRows(tabTitle, cols, header.length)
  const data = [...instrRows, header, ...dataRows]
  const ws = XLSX.utils.aoa_to_sheet(data)
  ws['!cols'] = colWidths.map((wch) => ({ wch }))
  ws['!merges'] = merges
  const dataStartRow = INSTRUCTION_ROW_COUNT + 2 // = 5
  if (dropdownFn) dropdownFn(ws, dataStartRow)
  flushDropdowns(ws)
  return ws
}

// Number of instruction rows prepended before the header row on each data tab
const INSTRUCTION_ROW_COUNT = 3  // rows 1-3 = instructions, row 4 = header, row 5+ = data

// ── Row mappers ───────────────────────────────────────────────────────────────

function teacherRows(teachers: ParsedTeacher[]): any[][] {
  return teachers.map((t) => [
    t.name,
    t.email ?? '',
    t.phone ?? '',
    t.teacher_type,
  ])
}

function subjectRows(subjects: ParsedSubject[]): any[][] {
  return subjects.map((s) => [s.subject_name, s.subject_code ?? '', s.color_code ?? ''])
}

function classRows(classes: ParsedClass[]): any[][] {
  return classes.map((c) => [c.class_name, c.class_group, c.section, c.room ?? '', c.class_teacher ?? ''])
}

function roomRows(rooms: ParsedRoom[]): any[][] {
  return rooms.map((r) => [r.name, r.description ?? '', r.capacity ?? ''])
}

function classScheduleRows(schedules: ParsedClassSchedule[]): any[][] {
  return schedules.map((s) => [
    s.name,
    s.description ?? '',
    (s.working_days ?? ['MONDAY', 'TUESDAY', 'WEDNESDAY', 'THURSDAY', 'FRIDAY']).join(','),
  ])
}

function scheduleAssignmentRows(assignments: ParsedClassScheduleAssignment[]): any[][] {
  return assignments.map((a) => [a.class_name, a.schedule_name])
}

function teacherSubjectRows(ts: ParsedTeacherSubject[]): any[][] {
  return ts.map((r) => [r.teacher_name, r.subject_name])
}

function teacherClassRows(tc: ParsedTeacherClass[]): any[][] {
  return tc.map((r) => [r.teacher_name, r.class_name])
}

function subjectClassRows(sc: ParsedSubjectClass[]): any[][] {
  return sc.map((r) => [r.subject_name, r.class_name])
}

// ── Helper: append a sheet ────────────────────────────────────────────────────

function appendSheet(wb: XLSX.WorkBook, name: string, header: string[], rows: any[][]): void {
  const data = [header, ...rows]
  const ws = XLSX.utils.aoa_to_sheet(data)
  ws['!cols'] = header.map(() => ({ wch: 20 }))
  XLSX.utils.book_append_sheet(wb, ws, name)
}

// ── 4.1 generateMasterDataTemplate ───────────────────────────────────────────

export function generateMasterDataTemplate(
  masterData: MasterDataSnapshot,
  teacherTypes: string[] = ['Permanent', 'Contract', 'Guest', 'Part-Time', 'General']
): Blob {
  const wb = XLSX.utils.book_new()

  // Instructions tab
  XLSX.utils.book_append_sheet(wb, buildInstructionsSheet(), 'Instructions')

  const dataStartRow = INSTRUCTION_ROW_COUNT + 2 // = 5

  // ── Teachers tab ──────────────────────────────────────────────────────────
  XLSX.utils.book_append_sheet(wb, makeTabSheet(
    'Teachers',
    [
      { name: 'Name',        required: true,  description: 'Full name of the teacher',         possibleValues: 'Any text',                                          example: 'John Smith' },
      { name: 'Email',       required: false, description: 'Teacher email address',            possibleValues: 'Valid email format',                                example: 'john@school.edu' },
      { name: 'Phone',       required: false, description: 'Contact phone number',             possibleValues: 'Any text',                                          example: '+1234567890' },
      { name: 'Teacher_Type',required: true,  description: 'Category — select from dropdown', possibleValues: teacherTypes.join(' / '),                            example: teacherTypes[0] ?? 'Permanent' },
    ],
    TEACHER_COLS,
    teacherRows(masterData.teachers),
    [28, 28, 18, 22],
    (ws) => addDropdown(ws, `D${dataStartRow}:D1000`, teacherTypes)
  ), 'Teachers')

  // ── Subjects tab ──────────────────────────────────────────────────────────
  XLSX.utils.book_append_sheet(wb, makeTabSheet(
    'Subjects',
    [
      { name: 'Subject_Name', required: true,  description: 'Full name of the subject',      possibleValues: 'Any text',              example: 'Mathematics' },
      { name: 'Subject_Code', required: false, description: 'Short code for the subject',    possibleValues: 'Any text (short)',      example: 'MATH' },
      { name: 'Color_Code',   required: false, description: 'Hex color for subject display', possibleValues: 'Hex color e.g. #FF5733', example: '#4CAF50' },
    ],
    SUBJECT_COLS,
    subjectRows(masterData.subjects),
    [28, 18, 18]
  ), 'Subjects')

  // ── Classes tab ───────────────────────────────────────────────────────────
  {
    const teacherNames = masterData.teachers.map((t) => t.name)
    XLSX.utils.book_append_sheet(wb, makeTabSheet(
      'Classes',
      [
        { name: 'Class_Name',    required: true,  description: 'Class or grade name',                          possibleValues: 'Any text — e.g. Grade 1, Class 5',                    example: 'Grade 1' },
        { name: 'Division',      required: true,  description: 'Division name (e.g. Primary, Secondary)',      possibleValues: 'Any text — e.g. Primary / Secondary / Junior / Senior', example: 'Primary' },
        { name: 'Section',       required: true,  description: 'Section letter — select from dropdown',        possibleValues: VALID_SECTIONS.join(' / '),                             example: 'A' },
        { name: 'Room',          required: false, description: 'Room number or name',                          possibleValues: 'Any text',                                            example: 'Room 101' },
        { name: 'Class_Teacher', required: false, description: 'Class teacher — select from dropdown',         possibleValues: 'Must exist in Teachers tab',                          example: 'John Smith' },
      ],
      CLASS_COLS,
      classRows(masterData.classes),
      [22, 22, 14, 18, 26],
      (ws) => {
        addDropdown(ws, `C${dataStartRow}:C1000`, VALID_SECTIONS)
        if (teacherNames.length > 0) addDropdown(ws, `E${dataStartRow}:E1000`, teacherNames)
      }
    ), 'Classes')
  }

  // ── Rooms tab ─────────────────────────────────────────────────────────────
  XLSX.utils.book_append_sheet(wb, makeTabSheet(
    'Rooms',
    [
      { name: 'Room_Name',   required: true,  description: 'Unique name for this room', possibleValues: 'Any text',  example: 'Room 101' },
      { name: 'Description', required: false, description: 'Short description',         possibleValues: 'Any text',  example: 'Science Lab' },
      { name: 'Capacity',    required: false, description: 'Max number of students',    possibleValues: 'Integer',   example: '30' },
    ],
    ROOM_COLS,
    roomRows(masterData.rooms ?? []),
    [28, 32, 14]
  ), 'Rooms')

  // ── Class-Schedules tab ───────────────────────────────────────────────────
  {
    const scheduleNames = masterData.schedules.map((s) => s.name)
    XLSX.utils.book_append_sheet(wb, makeTabSheet(
      'Class-Schedules',
      [
        { name: 'Schedule_Name', required: true,  description: 'Unique name for this schedule',                    possibleValues: 'Any text',                                                    example: 'Primary Schedule' },
        { name: 'Description',   required: false, description: 'Short description for admins',                     possibleValues: 'Any text',                                                    example: 'For grades 1-5' },
        { name: 'Working_Days',  required: true,  description: 'Comma-separated working days in UPPER CASE',       possibleValues: 'MONDAY,TUESDAY,WEDNESDAY,THURSDAY,FRIDAY (any combination)',  example: 'MONDAY,TUESDAY,WEDNESDAY,THURSDAY,FRIDAY' },
      ],
      CLASS_SCHEDULE_COLS,
      classScheduleRows(masterData.schedules),
      [28, 28, 52],
      (ws) => {
        if (scheduleNames.length > 0) addDropdown(ws, `A${dataStartRow}:A1000`, scheduleNames)
      }
    ), 'Class-Schedules')
  }

  // ── Schedule-Periods tab ──────────────────────────────────────────────────
  {
    const scheduleNames = masterData.schedules.map((s) => s.name)
    const allPeriodRows = masterData.schedules.flatMap((s) =>
      s.periods.map((p) => [
        s.name,
        p.period_number,
        String(p.start_time),
        String(p.end_time),
        p.is_break ? 'TRUE' : 'FALSE',
        p.period_name ?? '',
        p.break_type ?? '',
      ])
    )
    const ws = makeTabSheet(
      'Schedule-Periods',
      [
        { name: 'Schedule_Name', required: true,  description: 'Must match a Schedule_Name from Class-Schedules tab', possibleValues: 'Must exist in Class-Schedules tab', example: 'Primary Schedule' },
        { name: 'Period_Number', required: true,  description: 'Numeric period index (1, 2, 3 …)',                    possibleValues: 'Integer >= 1',                      example: '1' },
        { name: 'Start_Time',    required: true,  description: 'Period start time — type as text HH:MM',             possibleValues: 'HH:MM (24-hour)',                   example: '08:00' },
        { name: 'End_Time',      required: true,  description: 'Period end time — type as text HH:MM',               possibleValues: 'HH:MM (24-hour)',                   example: '08:45' },
        { name: 'Is_Break',      required: true,  description: 'Is this a break/recess? — select dropdown',          possibleValues: 'TRUE / FALSE',                      example: 'FALSE' },
        { name: 'Period_Name',   required: false, description: 'Optional label for this period',                     possibleValues: 'Any text e.g. Lunch Break',         example: 'Lunch Break' },
        { name: 'Break_Type',    required: false, description: 'Type of break — select dropdown',                    possibleValues: 'short / lunch / recess',            example: 'lunch' },
      ],
      PERIOD_COLS,
      allPeriodRows,
      [28, 16, 14, 14, 12, 22, 14],
      (ws2) => {
        if (scheduleNames.length > 0) addDropdown(ws2, `A${dataStartRow}:A1000`, scheduleNames)
        addDropdown(ws2, `E${dataStartRow}:E1000`, ['TRUE', 'FALSE'])
        addDropdown(ws2, `G${dataStartRow}:G1000`, ['short', 'lunch', 'recess'])
      }
    )
    forceStringColumn(ws, 'C', dataStartRow, dataStartRow + allPeriodRows.length + 50)
    forceStringColumn(ws, 'D', dataStartRow, dataStartRow + allPeriodRows.length + 50)
    XLSX.utils.book_append_sheet(wb, ws, 'Schedule-Periods')
  }

  // ── Schedule-Assignments tab ──────────────────────────────────────────────
  {
    const classNames = [...new Set(masterData.classes.map((c) => c.class_name))]
    const scheduleNames = masterData.schedules.map((s) => s.name)
    XLSX.utils.book_append_sheet(wb, makeTabSheet(
      'Schedule-Assignments',
      [
        { name: 'Class_Name',    required: true, description: 'Must match a Class_Name from Classes tab',          possibleValues: 'Must exist in Classes tab',          example: 'Grade 1' },
        { name: 'Schedule_Name', required: true, description: 'Must match a Schedule_Name from Class-Schedules tab', possibleValues: 'Must exist in Class-Schedules tab', example: 'Primary Schedule' },
      ],
      SCHEDULE_ASSIGNMENT_COLS,
      scheduleAssignmentRows(masterData.scheduleAssignments ?? []),
      [28, 28],
      (ws) => {
        if (classNames.length > 0) addDropdown(ws, `A${dataStartRow}:A1000`, classNames)
        if (scheduleNames.length > 0) addDropdown(ws, `B${dataStartRow}:B1000`, scheduleNames)
      }
    ), 'Schedule-Assignments')
  }

  // ── Teacher-Subjects tab ──────────────────────────────────────────────────
  {
    const teacherNames = masterData.teachers.map((t) => t.name)
    const subjectNames = masterData.subjects.map((s) => s.subject_name)
    XLSX.utils.book_append_sheet(wb, makeTabSheet(
      'Teacher-Subjects',
      [
        { name: 'Teacher_Name', required: true, description: 'Must exactly match a Name from the Teachers tab',        possibleValues: 'Must exist in Teachers tab',  example: 'John Smith' },
        { name: 'Subject_Name', required: true, description: 'Must exactly match a Subject_Name from the Subjects tab', possibleValues: 'Must exist in Subjects tab', example: 'Mathematics' },
      ],
      TEACHER_SUBJECT_COLS,
      teacherSubjectRows(masterData.teacherSubjects),
      [32, 32],
      (ws) => {
        if (teacherNames.length > 0) addDropdown(ws, `A${dataStartRow}:A1000`, teacherNames)
        if (subjectNames.length > 0) addDropdown(ws, `B${dataStartRow}:B1000`, subjectNames)
      }
    ), 'Teacher-Subjects')
  }

  // ── Teacher-Class tab ─────────────────────────────────────────────────────
  {
    const teacherNames = masterData.teachers.map((t) => t.name)
    const classNames = [...new Set(masterData.classes.map((c) => c.class_name))]
    XLSX.utils.book_append_sheet(wb, makeTabSheet(
      'Teacher-Class',
      [
        { name: 'Teacher_Name', required: true, description: 'Must exactly match a Name from the Teachers tab', possibleValues: 'Must exist in Teachers tab', example: 'John Smith' },
        { name: 'Class_Name',   required: true, description: 'Must exactly match a Class_Name from the Classes tab', possibleValues: 'Must exist in Classes tab', example: 'Grade 1' },
      ],
      TEACHER_CLASS_COLS,
      teacherClassRows(masterData.teacherClasses ?? []),
      [32, 32],
      (ws) => {
        if (teacherNames.length > 0) addDropdown(ws, `A${dataStartRow}:A1000`, teacherNames)
        if (classNames.length > 0) addDropdown(ws, `B${dataStartRow}:B1000`, classNames)
      }
    ), 'Teacher-Class')
  }

  // ── Subject-Class tab ─────────────────────────────────────────────────────
  {
    const subjectNames = masterData.subjects.map((s) => s.subject_name)
    const classNames = [...new Set(masterData.classes.map((c) => c.class_name))]
    XLSX.utils.book_append_sheet(wb, makeTabSheet(
      'Subject-Class',
      [
        { name: 'Subject_Name', required: true, description: 'Must exactly match a Subject_Name from the Subjects tab', possibleValues: 'Must exist in Subjects tab', example: 'Mathematics' },
        { name: 'Class_Name',   required: true, description: 'Must exactly match a Class_Name from the Classes tab',    possibleValues: 'Must exist in Classes tab',  example: 'Grade 1' },
      ],
      SUBJECT_CLASS_COLS,
      subjectClassRows(masterData.subjectClasses ?? []),
      [32, 32],
      (ws) => {
        if (subjectNames.length > 0) addDropdown(ws, `A${dataStartRow}:A1000`, subjectNames)
        if (classNames.length > 0) addDropdown(ws, `B${dataStartRow}:B1000`, classNames)
      }
    ), 'Subject-Class')
  }

  const buf = XLSX.write(wb, { bookType: 'xlsx', type: 'array' })
  return new Blob([buf], { type: XLSX_MIME })
}

function readFileAsArrayBuffer(file: File): Promise<ArrayBuffer> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onload = (e) => resolve(e.target!.result as ArrayBuffer)
    reader.onerror = () => reject(new Error('Failed to read file'))
    reader.readAsArrayBuffer(file)
  })
}

function sheetToObjects<T>(ws: XLSX.WorkSheet): T[] {
  // Skip instruction rows — header is at row index INSTRUCTION_ROW_COUNT (0-based)
  return XLSX.utils.sheet_to_json<T>(ws, { defval: '', range: INSTRUCTION_ROW_COUNT })
}

export async function parseMasterDataWorkbook(file: File): Promise<ParsedMasterData> {
  const buf = await readFileAsArrayBuffer(file)
  const wb = XLSX.read(buf, { type: 'array' })

  // Teachers
  const teachersWs = wb.Sheets['Teachers']
  const teachers: ParsedTeacher[] = teachersWs
    ? sheetToObjects<any>(teachersWs).map((r) => ({
        name: String(r['Name'] ?? '').trim(),
        email: r['Email'] ? String(r['Email']).trim() : undefined,
        phone: r['Phone'] ? String(r['Phone']).trim() : undefined,
        teacher_type: String(r['Teacher_Type'] ?? '').trim(),
      }))
    : []

  // Subjects
  const subjectsWs = wb.Sheets['Subjects']
  const subjects: ParsedSubject[] = subjectsWs
    ? sheetToObjects<any>(subjectsWs).map((r) => ({
        subject_name: String(r['Subject_Name'] ?? '').trim(),
        subject_code: r['Subject_Code'] ? String(r['Subject_Code']).trim() : undefined,
        color_code: r['Color_Code'] ? String(r['Color_Code']).trim() : undefined,
      }))
    : []

  // Classes
  const classesWs = wb.Sheets['Classes']
  const classes: ParsedClass[] = classesWs
    ? sheetToObjects<any>(classesWs).map((r) => ({
        class_name: String(r['Class_Name'] ?? '').trim(),
        class_group: String(r['Division'] ?? '').trim(),
        section: String(r['Section'] ?? '').trim(),
        room: r['Room'] ? String(r['Room']).trim() : undefined,
        class_teacher: r['Class_Teacher'] ? String(r['Class_Teacher']).trim() : undefined,
      }))
    : []

  // Rooms
  const roomsWs = wb.Sheets['Rooms']
  const rooms: ParsedRoom[] = roomsWs
    ? sheetToObjects<any>(roomsWs)
        .map((r) => ({
          name: String(r['Room_Name'] ?? '').trim(),
          description: r['Description'] ? String(r['Description']).trim() : undefined,
          capacity: r['Capacity'] ? Number(r['Capacity']) : undefined,
        }))
        .filter((r) => r.name)
    : []

  // Class-Schedules + Schedule-Periods (new structured format)
  // Also supports legacy 'Periods' tab for backward compatibility
  let schedules: ParsedClassSchedule[]

  const classSchedulesWs = wb.Sheets['Class-Schedules']
  const schedulePeriodsWs = wb.Sheets['Schedule-Periods']

  if (classSchedulesWs && schedulePeriodsWs) {
    // New format: parse schedule metadata + periods grouped by schedule name
    const rawSchedules = sheetToObjects<any>(classSchedulesWs)
    const rawPeriods = sheetToObjects<any>(schedulePeriodsWs)

    // Group periods by schedule name
    const periodsBySchedule = new Map<string, ParsedPeriod[]>()
    rawPeriods.forEach((r) => {
      const sName = String(r['Schedule_Name'] ?? '').trim()
      if (!sName) return
      if (!periodsBySchedule.has(sName)) periodsBySchedule.set(sName, [])
      periodsBySchedule.get(sName)!.push({
        period_number: Number(r['Period_Number']),
        start_time: String(r['Start_Time'] ?? '').trim(),
        end_time: String(r['End_Time'] ?? '').trim(),
        is_break: String(r['Is_Break'] ?? '').toUpperCase() === 'TRUE',
        period_name: r['Period_Name'] ? String(r['Period_Name']).trim() : undefined,
        break_type: r['Break_Type'] ? (String(r['Break_Type']).trim() as 'short' | 'lunch' | 'recess') : undefined,
      })
    })

    schedules = rawSchedules
      .map((r): ParsedClassSchedule | null => {
        const name = String(r['Schedule_Name'] ?? '').trim()
        if (!name) return null
        const workingDaysRaw = String(r['Working_Days'] ?? '').trim()
        const working_days = workingDaysRaw
          ? workingDaysRaw.split(',').map((d) => d.trim().toUpperCase()).filter(Boolean)
          : ['MONDAY', 'TUESDAY', 'WEDNESDAY', 'THURSDAY', 'FRIDAY']
        return {
          name,
          description: r['Description'] ? String(r['Description']).trim() : undefined,
          working_days,
          periods: periodsBySchedule.get(name) ?? [],
        }
      })
      .filter((s): s is ParsedClassSchedule => s !== null)
  } else {
    // Legacy fallback: flat 'Periods' tab → single 'Default' schedule
    const periodsWs = wb.Sheets['Periods']
    const periods: ParsedPeriod[] = periodsWs
      ? sheetToObjects<any>(periodsWs).map((r) => ({
          period_number: Number(r['Period_Number']),
          start_time: String(r['Start_Time'] ?? '').trim(),
          end_time: String(r['End_Time'] ?? '').trim(),
          is_break: String(r['Is_Break'] ?? '').toUpperCase() === 'TRUE',
          period_name: r['Period_Name'] ? String(r['Period_Name']).trim() : undefined,
          break_type: r['Break_Type'] ? (String(r['Break_Type']).trim() as 'short' | 'lunch' | 'recess') : undefined,
        }))
      : []
    schedules = [{ name: 'Default', periods }]
  }

  // Schedule-Assignments
  const scheduleAssignmentsWs = wb.Sheets['Schedule-Assignments']
  const scheduleAssignments: ParsedClassScheduleAssignment[] = scheduleAssignmentsWs
    ? sheetToObjects<any>(scheduleAssignmentsWs)
        .map((r) => ({
          class_name: String(r['Class_Name'] ?? '').trim(),
          schedule_name: String(r['Schedule_Name'] ?? '').trim(),
        }))
        .filter((a) => a.class_name && a.schedule_name)
    : []

  // Teacher-Subjects
  const tsWs = wb.Sheets['Teacher-Subjects']
  const teacherSubjects: ParsedTeacherSubject[] = tsWs
    ? sheetToObjects<any>(tsWs).map((r) => ({
        teacher_name: String(r['Teacher_Name'] ?? '').trim(),
        subject_name: String(r['Subject_Name'] ?? '').trim(),
      }))
    : []

  // Teacher-Class
  const tcWs = wb.Sheets['Teacher-Class']
  const teacherClasses: ParsedTeacherClass[] = tcWs
    ? sheetToObjects<any>(tcWs)
        .map((r) => ({
          teacher_name: String(r['Teacher_Name'] ?? '').trim(),
          class_name: String(r['Class_Name'] ?? '').trim(),
        }))
        .filter((r) => r.teacher_name && r.class_name)
    : []

  // Subject-Class
  const scWs = wb.Sheets['Subject-Class']
  const subjectClasses: ParsedSubjectClass[] = scWs
    ? sheetToObjects<any>(scWs)
        .map((r) => ({
          subject_name: String(r['Subject_Name'] ?? '').trim(),
          class_name: String(r['Class_Name'] ?? '').trim(),
        }))
        .filter((r) => r.subject_name && r.class_name)
    : []

  return { teachers, subjects, classes, rooms, schedules, scheduleAssignments, teacherSubjects, teacherClasses, subjectClasses }
}

// ── 4.4 validateMasterData ────────────────────────────────────────────────────

export function validateMasterData(data: ParsedMasterData): MasterDataValidationReport {
  const errors: ValidationError[] = []
  const warnings: ValidationError[] = []

  // Row offset: instruction rows + header row + 1 (Excel is 1-indexed)
  // instruction rows = INSTRUCTION_ROW_COUNT, header = row 5, data starts row 6
  const rowOffset = INSTRUCTION_ROW_COUNT + 1  // index i=0 → Excel row 6

  // Teachers — required: Name, Teacher_Type
  const teacherNames = new Set<string>()
  data.teachers.forEach((t, i) => {
    const row = i + rowOffset + 1
    if (!t.name) errors.push({ tab: 'Teachers', row, column: 'Name', message: 'Name is required' })
    if (!t.teacher_type) errors.push({ tab: 'Teachers', row, column: 'Teacher_Type', message: 'Teacher_Type is required' })
    if (t.name) {
      if (teacherNames.has(t.name)) warnings.push({ tab: 'Teachers', row, column: 'Name', message: `Duplicate teacher name: "${t.name}"` })
      teacherNames.add(t.name)
    }
  })

  // Subjects — required: Subject_Name
  const subjectNames = new Set<string>()
  data.subjects.forEach((s, i) => {
    const row = i + rowOffset + 1
    if (!s.subject_name) errors.push({ tab: 'Subjects', row, column: 'Subject_Name', message: 'Subject_Name is required' })
    if (s.subject_name) {
      if (subjectNames.has(s.subject_name)) warnings.push({ tab: 'Subjects', row, column: 'Subject_Name', message: `Duplicate subject name: "${s.subject_name}"` })
      subjectNames.add(s.subject_name)
    }
  })

  // Classes — required: Class_Name, class_group, Section
  const classSectionKeys = new Set<string>()
  data.classes.forEach((c, i) => {
    const row = i + rowOffset + 1
    if (!c.class_name) errors.push({ tab: 'Classes', row, column: 'Class_Name', message: 'Class_Name is required' })
    if (!c.class_group) errors.push({ tab: 'Classes', row, column: 'Division', message: 'Division is required' })
    if (!c.section) errors.push({ tab: 'Classes', row, column: 'Section', message: 'Section is required' })
    const key = `${c.class_name}|${c.class_group}|${c.section}`
    if (c.class_name && c.section) {
      if (classSectionKeys.has(key)) warnings.push({ tab: 'Classes', row, column: 'Section', message: `Duplicate class-division-section: "${c.class_name}-${c.class_group}-${c.section}"` })
      classSectionKeys.add(key)
    }
  })

  // Periods — required: Period_Number, Start_Time, End_Time
  const allPeriods = data.schedules.flatMap(s => s.periods)
  allPeriods.forEach((p, i) => {
    const row = i + rowOffset + 1
    if (p.period_number === undefined || p.period_number === null || isNaN(p.period_number))
      errors.push({ tab: 'Periods', row, column: 'Period_Number', message: 'Period_Number must be a number' })
    if (!p.start_time) errors.push({ tab: 'Periods', row, column: 'Start_Time', message: 'Start_Time is required' })
    if (!p.end_time) errors.push({ tab: 'Periods', row, column: 'End_Time', message: 'End_Time is required' })
  })

  // Teacher-Subjects — cross-reference + required fields
  data.teacherSubjects.forEach((ts, i) => {
    const row = i + rowOffset + 1
    if (!ts.teacher_name) {
      errors.push({ tab: 'Teacher-Subjects', row, column: 'Teacher_Name', message: 'Teacher_Name is required' })
    } else if (!teacherNames.has(ts.teacher_name)) {
      errors.push({ tab: 'Teacher-Subjects', row, column: 'Teacher_Name', message: `Teacher "${ts.teacher_name}" not found in Teachers tab` })
    }
    if (!ts.subject_name) {
      errors.push({ tab: 'Teacher-Subjects', row, column: 'Subject_Name', message: 'Subject_Name is required' })
    } else if (!subjectNames.has(ts.subject_name)) {
      errors.push({ tab: 'Teacher-Subjects', row, column: 'Subject_Name', message: `Subject "${ts.subject_name}" not found in Subjects tab` })
    }
  })

  return { valid: errors.length === 0, errors, warnings }
}

// ── 4.7 generateTimetableTemplate ────────────────────────────────────────────

function buildTimetableSheet(
  wb: XLSX.WorkBook,
  sheetName: string,
  cls: ParsedClass,
  teacherNames: string[],
  subjectNames: string[],
  periodNumbers: string[],
  sections: string[]
): void {
  const ws = XLSX.utils.aoa_to_sheet([TIMETABLE_COLS])
  ws['!cols'] = TIMETABLE_COLS.map(() => ({ wch: 18 }))

  // Day dropdown (col C)
  addDropdown(ws, 'C2:C1000', VALID_DAYS)
  // Period dropdown (col D)
  if (periodNumbers.length > 0) addDropdown(ws, 'D2:D1000', periodNumbers)
  // Teacher dropdown (col E)
  if (teacherNames.length > 0) addDropdown(ws, 'E2:E1000', teacherNames)
  // Subject dropdown (col F)
  if (subjectNames.length > 0) addDropdown(ws, 'F2:F1000', subjectNames)

  // Pre-fill Class (col A) and Section (col B) — Section dropdown too
  if (sections.length > 0) addDropdown(ws, 'B2:B1000', sections)
  const exampleRow = [cls.class_name, cls.section, '', '', '', '']
  XLSX.utils.sheet_add_aoa(ws, [exampleRow], { origin: 'A2' })

  flushDropdowns(ws)
  XLSX.utils.book_append_sheet(wb, ws, sheetName.slice(0, 31))
}

export function generateTimetableTemplate(
  masterData: ParsedMasterData,
  mode: 'single' | 'per-class'
): Blob | Blob[] {
  const teacherNames = masterData.teachers.map((t) => t.name)
  const subjectNames = masterData.subjects.map((s) => s.subject_name)
  // Unique period numbers across all schedules, sorted
  const periodNumbers = Array.from(
    new Set(masterData.schedules.flatMap(s => s.periods).map((p) => String(p.period_number)))
  ).sort((a, b) => Number(a) - Number(b))
  // All unique sections across classes
  const allSections = Array.from(new Set(masterData.classes.map((c) => c.section))).sort()

  if (mode === 'single') {
    const wb = XLSX.utils.book_new()
    masterData.classes.forEach((cls) => {
      const sheetName = `${cls.class_name}-${cls.section}`
      buildTimetableSheet(wb, sheetName, cls, teacherNames, subjectNames, periodNumbers, allSections)
    })
    const buf = XLSX.write(wb, { bookType: 'xlsx', type: 'array' })
    return new Blob([buf], { type: XLSX_MIME })
  } else {
    return masterData.classes.map((cls) => {
      const wb = XLSX.utils.book_new()
      const sheetName = `${cls.class_name}-${cls.section}`
      buildTimetableSheet(wb, sheetName, cls, teacherNames, subjectNames, periodNumbers, allSections)
      const buf = XLSX.write(wb, { bookType: 'xlsx', type: 'array' })
      return new Blob([buf], { type: XLSX_MIME })
    })
  }
}

// ── 4.9 parseTimetableWorkbook ────────────────────────────────────────────────

export interface TimetableParseResult {
  rows: TimetableRow[]
  errors: ValidationError[]
}

export async function parseTimetableWorkbook(
  file: File,
  masterData: ParsedMasterData
): Promise<TimetableParseResult> {
  const buf = await readFileAsArrayBuffer(file)
  const wb = XLSX.read(buf, { type: 'array' })

  const teacherNames = new Set(masterData.teachers.map((t) => t.name))
  const subjectNames = new Set(masterData.subjects.map((s) => s.subject_name))
  const classSectionKeys = new Set(masterData.classes.map((c) => `${c.class_name}|${c.section}`))

  const rows: TimetableRow[] = []
  const errors: ValidationError[] = []

  for (const sheetName of wb.SheetNames) {
    const ws = wb.Sheets[sheetName]
    const rawRows = XLSX.utils.sheet_to_json<any>(ws, { defval: '' })

    rawRows.forEach((r: any, i: number) => {
      const rowNum = i + 2
      const cls = String(r['Class'] ?? '').trim()
      const section = String(r['Section'] ?? '').trim()
      const day = String(r['Day'] ?? '').trim()
      const period = Number(r['Period'])
      const teacher = String(r['Teacher'] ?? '').trim()
      const subject = String(r['Subject'] ?? '').trim()

      let rowValid = true

      if (!cls) { errors.push({ filename: file.name, sheet: sheetName, row: rowNum, column: 'Class', message: 'Class is required' }); rowValid = false }
      if (!section) { errors.push({ filename: file.name, sheet: sheetName, row: rowNum, column: 'Section', message: 'Section is required' }); rowValid = false }
      if (!day) {
        errors.push({ filename: file.name, sheet: sheetName, row: rowNum, column: 'Day', message: 'Day is required' }); rowValid = false
      } else if (!VALID_DAYS.includes(day)) {
        errors.push({ filename: file.name, sheet: sheetName, row: rowNum, column: 'Day', message: `Invalid day "${day}". Must be one of: ${VALID_DAYS.join(', ')}` }); rowValid = false
      }
      if (isNaN(period)) { errors.push({ filename: file.name, sheet: sheetName, row: rowNum, column: 'Period', message: 'Period must be a number' }); rowValid = false }
      if (!teacher) {
        errors.push({ filename: file.name, sheet: sheetName, row: rowNum, column: 'Teacher', message: 'Teacher is required' }); rowValid = false
      } else if (!teacherNames.has(teacher)) {
        errors.push({ filename: file.name, sheet: sheetName, row: rowNum, column: 'Teacher', message: `Teacher "${teacher}" not found in master data` }); rowValid = false
      }
      if (!subject) {
        errors.push({ filename: file.name, sheet: sheetName, row: rowNum, column: 'Subject', message: 'Subject is required' }); rowValid = false
      } else if (!subjectNames.has(subject)) {
        errors.push({ filename: file.name, sheet: sheetName, row: rowNum, column: 'Subject', message: `Subject "${subject}" not found in master data` }); rowValid = false
      }
      if (cls && section && !classSectionKeys.has(`${cls}|${section}`)) {
        errors.push({ filename: file.name, sheet: sheetName, row: rowNum, column: 'Section', message: `Class-section "${cls}-${section}" not found in master data` }); rowValid = false
      }

      if (rowValid) rows.push({ class: cls, section, day, period, teacher, subject })
    })
  }

  return { rows, errors }
}

// ── 4.12 generateFullExportWorkbook ──────────────────────────────────────────

export function generateFullExportWorkbook(exportData: ExportData): Blob {
  const wb = XLSX.utils.book_new()
  const teacherTypes = [...new Set(exportData.teachers.map(t => t.teacher_type).filter(Boolean))]
  const teacherNames = exportData.teachers.map(t => t.name)
  const subjectNames = exportData.subjects.map(s => s.subject_name)
  const dataStartRow = INSTRUCTION_ROW_COUNT + 2 // = 5

  // Instructions tab
  XLSX.utils.book_append_sheet(wb, buildInstructionsSheet(), 'Instructions')

  // Teachers — styled with dropdowns
  XLSX.utils.book_append_sheet(wb, makeTabSheet(
    'Teachers',
    [
      { name: 'Name',        required: true,  description: 'Full name of the teacher',         possibleValues: 'Any text',                    example: 'John Smith' },
      { name: 'Email',       required: false, description: 'Teacher email address',            possibleValues: 'Valid email format',           example: 'john@school.edu' },
      { name: 'Phone',       required: false, description: 'Contact phone number',             possibleValues: 'Any text',                    example: '+1234567890' },
      { name: 'Teacher_Type',required: true,  description: 'Category — select from dropdown', possibleValues: teacherTypes.join(' / ') || 'Permanent / Contract / Guest', example: teacherTypes[0] ?? 'Permanent' },
    ],
    TEACHER_COLS,
    teacherRows(exportData.teachers),
    [28, 28, 18, 22],
    (ws) => addDropdown(ws, `D${dataStartRow}:D1000`, teacherTypes.length > 0 ? teacherTypes : ['Permanent', 'Contract', 'Guest', 'Part-Time', 'General'])
  ), 'Teachers')

  // Subjects — styled
  XLSX.utils.book_append_sheet(wb, makeTabSheet(
    'Subjects',
    [
      { name: 'Subject_Name', required: true,  description: 'Full name of the subject',      possibleValues: 'Any text',              example: 'Mathematics' },
      { name: 'Subject_Code', required: false, description: 'Short code for the subject',    possibleValues: 'Any text (short)',      example: 'MATH' },
      { name: 'Color_Code',   required: false, description: 'Hex color for subject display', possibleValues: 'Hex color e.g. #FF5733', example: '#4CAF50' },
    ],
    SUBJECT_COLS,
    subjectRows(exportData.subjects),
    [28, 18, 18]
  ), 'Subjects')

  // Classes — styled with dropdowns
  XLSX.utils.book_append_sheet(wb, makeTabSheet(
    'Classes',
    [
      { name: 'Class_Name',    required: true,  description: 'Class or grade name',           possibleValues: 'Any text',                    example: 'Grade 1' },
      { name: 'Division',      required: true,  description: 'Division name',                 possibleValues: 'Any text',                    example: 'Primary' },
      { name: 'Section',       required: true,  description: 'Section letter',                possibleValues: VALID_SECTIONS.join(' / '),     example: 'A' },
      { name: 'Room',          required: false, description: 'Room number or name',           possibleValues: 'Any text',                    example: 'Room 101' },
      { name: 'Class_Teacher', required: false, description: 'Class teacher name',            possibleValues: 'Must exist in Teachers tab',  example: teacherNames[0] ?? 'John Smith' },
    ],
    CLASS_COLS,
    classRows(exportData.classes),
    [22, 22, 14, 18, 26],
    (ws) => {
      addDropdown(ws, `C${dataStartRow}:C1000`, VALID_SECTIONS)
      if (teacherNames.length > 0) addDropdown(ws, `E${dataStartRow}:E1000`, teacherNames)
    }
  ), 'Classes')

  // Rooms — styled
  XLSX.utils.book_append_sheet(wb, makeTabSheet(
    'Rooms',
    [
      { name: 'Room_Name',   required: true,  description: 'Unique name for this room', possibleValues: 'Any text', example: 'Room 101' },
      { name: 'Description', required: false, description: 'Short description',         possibleValues: 'Any text', example: 'Science Lab' },
      { name: 'Capacity',    required: false, description: 'Max number of students',    possibleValues: 'Integer',  example: '30' },
    ],
    ROOM_COLS,
    roomRows(exportData.rooms),
    [28, 32, 14]
  ), 'Rooms')

  // Class-Schedules — styled
  {
    const scheduleNames = exportData.schedules.map((s) => s.name)
    XLSX.utils.book_append_sheet(wb, makeTabSheet(
      'Class-Schedules',
      [
        { name: 'Schedule_Name', required: true,  description: 'Unique name for this schedule',              possibleValues: 'Any text',                                                   example: 'Primary Schedule' },
        { name: 'Description',   required: false, description: 'Short description for admins',               possibleValues: 'Any text',                                                   example: 'For grades 1-5' },
        { name: 'Working_Days',  required: true,  description: 'Comma-separated working days in UPPER CASE', possibleValues: 'MONDAY,TUESDAY,WEDNESDAY,THURSDAY,FRIDAY (any combination)', example: 'MONDAY,TUESDAY,WEDNESDAY,THURSDAY,FRIDAY' },
      ],
      CLASS_SCHEDULE_COLS,
      classScheduleRows(exportData.schedules),
      [28, 28, 52],
      (ws) => {
        if (scheduleNames.length > 0) addDropdown(ws, `A${dataStartRow}:A1000`, scheduleNames)
      }
    ), 'Class-Schedules')
  }

  // Schedule-Periods — styled with dropdowns and forced string time columns
  {
    const scheduleNames = exportData.schedules.map((s) => s.name)
    const allPeriodRows = exportData.schedules.flatMap((s) =>
      s.periods.map((p) => [
        s.name,
        p.period_number,
        String(p.start_time),
        String(p.end_time),
        p.is_break ? 'TRUE' : 'FALSE',
        p.period_name ?? '',
        p.break_type ?? '',
      ])
    )
    const ws = makeTabSheet(
      'Schedule-Periods',
      [
        { name: 'Schedule_Name', required: true,  description: 'Must match a Schedule_Name from Class-Schedules tab', possibleValues: 'Must exist in Class-Schedules tab', example: scheduleNames[0] ?? 'Primary Schedule' },
        { name: 'Period_Number', required: true,  description: 'Numeric period index',                                possibleValues: 'Integer >= 1',                      example: '1' },
        { name: 'Start_Time',    required: true,  description: 'Period start time HH:MM',                            possibleValues: 'HH:MM (24-hour)',                   example: '08:00' },
        { name: 'End_Time',      required: true,  description: 'Period end time HH:MM',                              possibleValues: 'HH:MM (24-hour)',                   example: '08:45' },
        { name: 'Is_Break',      required: true,  description: 'Is this a break?',                                   possibleValues: 'TRUE / FALSE',                      example: 'FALSE' },
        { name: 'Period_Name',   required: false, description: 'Optional label for this period',                     possibleValues: 'Any text',                          example: 'Lunch Break' },
        { name: 'Break_Type',    required: false, description: 'Type of break',                                      possibleValues: 'short / lunch / recess',            example: 'lunch' },
      ],
      PERIOD_COLS,
      allPeriodRows,
      [28, 16, 14, 14, 12, 22, 14],
      (ws2) => {
        if (scheduleNames.length > 0) addDropdown(ws2, `A${dataStartRow}:A1000`, scheduleNames)
        addDropdown(ws2, `E${dataStartRow}:E1000`, ['TRUE', 'FALSE'])
        addDropdown(ws2, `G${dataStartRow}:G1000`, ['short', 'lunch', 'recess'])
      }
    )
    forceStringColumn(ws, 'C', dataStartRow, dataStartRow + allPeriodRows.length + 50)
    forceStringColumn(ws, 'D', dataStartRow, dataStartRow + allPeriodRows.length + 50)
    XLSX.utils.book_append_sheet(wb, ws, 'Schedule-Periods')
  }

  // Schedule-Assignments — styled with dropdowns
  {
    const classNames = [...new Set(exportData.classes.map((c) => c.class_name))]
    const scheduleNames = exportData.schedules.map((s) => s.name)
    XLSX.utils.book_append_sheet(wb, makeTabSheet(
      'Schedule-Assignments',
      [
        { name: 'Class_Name',    required: true, description: 'Must match a Class_Name from Classes tab',           possibleValues: 'Must exist in Classes tab',          example: classNames[0] ?? 'Grade 1' },
        { name: 'Schedule_Name', required: true, description: 'Must match a Schedule_Name from Class-Schedules tab', possibleValues: 'Must exist in Class-Schedules tab', example: scheduleNames[0] ?? 'Primary Schedule' },
      ],
      SCHEDULE_ASSIGNMENT_COLS,
      scheduleAssignmentRows(exportData.scheduleAssignments),
      [28, 28],
      (ws) => {
        if (classNames.length > 0) addDropdown(ws, `A${dataStartRow}:A1000`, classNames)
        if (scheduleNames.length > 0) addDropdown(ws, `B${dataStartRow}:B1000`, scheduleNames)
      }
    ), 'Schedule-Assignments')
  }

  // Teacher-Subjects — styled with dropdowns
  XLSX.utils.book_append_sheet(wb, makeTabSheet(
    'Teacher-Subjects',
    [
      { name: 'Teacher_Name', required: true, description: 'Must match a Name from Teachers tab', possibleValues: 'Must exist in Teachers tab',  example: teacherNames[0] ?? 'John Smith' },
      { name: 'Subject_Name', required: true, description: 'Must match a Subject_Name from Subjects tab', possibleValues: 'Must exist in Subjects tab', example: subjectNames[0] ?? 'Mathematics' },
    ],
    TEACHER_SUBJECT_COLS,
    teacherSubjectRows(exportData.teacherSubjects),
    [32, 32],
    (ws) => {
      if (teacherNames.length > 0) addDropdown(ws, `A${dataStartRow}:A1000`, teacherNames)
      if (subjectNames.length > 0) addDropdown(ws, `B${dataStartRow}:B1000`, subjectNames)
    }
  ), 'Teacher-Subjects')

  // Teacher-Class — styled with dropdowns
  {
    const classNames = [...new Set(exportData.classes.map((c) => c.class_name))]
    XLSX.utils.book_append_sheet(wb, makeTabSheet(
      'Teacher-Class',
      [
        { name: 'Teacher_Name', required: true, description: 'Must match a Name from Teachers tab', possibleValues: 'Must exist in Teachers tab', example: teacherNames[0] ?? 'John Smith' },
        { name: 'Class_Name',   required: true, description: 'Must match a Class_Name from Classes tab', possibleValues: 'Must exist in Classes tab', example: classNames[0] ?? 'Grade 1' },
      ],
      TEACHER_CLASS_COLS,
      teacherClassRows(exportData.teacherClasses),
      [32, 32],
      (ws) => {
        if (teacherNames.length > 0) addDropdown(ws, `A${dataStartRow}:A1000`, teacherNames)
        if (classNames.length > 0) addDropdown(ws, `B${dataStartRow}:B1000`, classNames)
      }
    ), 'Teacher-Class')
  }

  // Subject-Class — styled with dropdowns
  {
    const classNames = [...new Set(exportData.classes.map((c) => c.class_name))]
    XLSX.utils.book_append_sheet(wb, makeTabSheet(
      'Subject-Class',
      [
        { name: 'Subject_Name', required: true, description: 'Must match a Subject_Name from Subjects tab', possibleValues: 'Must exist in Subjects tab', example: subjectNames[0] ?? 'Mathematics' },
        { name: 'Class_Name',   required: true, description: 'Must match a Class_Name from Classes tab',    possibleValues: 'Must exist in Classes tab',  example: classNames[0] ?? 'Grade 1' },
      ],
      SUBJECT_CLASS_COLS,
      subjectClassRows(exportData.subjectClasses),
      [32, 32],
      (ws) => {
        if (subjectNames.length > 0) addDropdown(ws, `A${dataStartRow}:A1000`, subjectNames)
        if (classNames.length > 0) addDropdown(ws, `B${dataStartRow}:B1000`, classNames)
      }
    ), 'Subject-Class')
  }

  // Per-class timetable tabs
  if (exportData.timetableRows.length > 0) {
    const byClass = new Map<string, TimetableRow[]>()
    exportData.timetableRows.forEach((r) => {
      const key = `${r.class}|${r.section}`
      if (!byClass.has(key)) byClass.set(key, [])
      byClass.get(key)!.push(r)
    })
    byClass.forEach((rows, key) => {
      const [cls, section] = key.split('|')
      const sheetName = `${cls}-${section}`.slice(0, 31)
      const dataRows = rows.map((r) => [r.class, r.section, r.day, r.period, r.teacher, r.subject])
      appendSheet(wb, sheetName, TIMETABLE_COLS, dataRows)
    })
  }

  const buf = XLSX.write(wb, { bookType: 'xlsx', type: 'array' })
  return new Blob([buf], { type: XLSX_MIME })
}
